import { isApiNotFoundStatus } from '../../utils/api'

import {
  LIST_ITEM_NAVIGATION_DEFAULT_TOTAL,
  LIST_ITEM_NAVIGATION_EMPTY,
  ListItemNavigationDirection,
  ListItemNavigationField,
} from './useListItemNavigation.constants'

// The anchor is still listed: it is one neighbour, the other comes from its lookup
function getNeighboursAroundAnchor({ anchor, anchorData }) {
  return anchor.direction === ListItemNavigationDirection.Next
    ? {
        nextUuid: anchorData[ListItemNavigationField.NextUuid] ?? null,
        previousUuid: anchor.uuid,
      }
    : {
        nextUuid: anchor.uuid,
        previousUuid: anchorData[ListItemNavigationField.PreviousUuid] ?? null,
      }
}

// The anchor left the list too (e.g. read under the `unread` filter): only
// its far neighbour from the step-time snapshot is still worth offering
function getNeighboursBeyondAnchor(anchor) {
  return anchor.direction === ListItemNavigationDirection.Next
    ? { nextUuid: null, previousUuid: anchor.previousUuid ?? null }
    : { nextUuid: anchor.nextUuid ?? null, previousUuid: null }
}

// Only an explicit `index: null` marks an item that left the filtered list:
// backends without `index` / `total` (idwell-api before PR #1280) answer
// `{ previous_uuid, next_uuid }` only, which still counts as listed
export function isListItemNavigationItemListed(data) {
  return data?.[ListItemNavigationField.Index] !== null
}

export function isListItemNavigationNotFoundError(error) {
  return isApiNotFoundStatus(error?.response?.status)
}

export function getListItemNavigationListAnchor({ ids, uuid }) {
  const index = ids.indexOf(uuid)
  const previousUuid = ids[index - 1]
  const nextUuid = ids[index + 1]

  if (previousUuid) {
    return {
      direction: ListItemNavigationDirection.Next,
      nextUuid: uuid,
      previousUuid: ids[index - 2] ?? null,
      uuid: previousUuid,
    }
  }

  if (nextUuid) {
    return {
      direction: ListItemNavigationDirection.Previous,
      nextUuid: ids[index + 2] ?? null,
      previousUuid: uuid,
      uuid: nextUuid,
    }
  }

  return null
}

export function getListItemNavigationNeighbours({ anchor, anchorData, data }) {
  const total =
    data[ListItemNavigationField.Total] ?? LIST_ITEM_NAVIGATION_DEFAULT_TOTAL

  if (isListItemNavigationItemListed(data)) {
    return {
      index: data[ListItemNavigationField.Index] ?? null,
      nextUuid: data[ListItemNavigationField.NextUuid] ?? null,
      previousUuid: data[ListItemNavigationField.PreviousUuid] ?? null,
      total,
    }
  }

  if (!anchor) {
    return { ...LIST_ITEM_NAVIGATION_EMPTY, total }
  }

  return {
    index: null,
    total,
    ...(isListItemNavigationItemListed(anchorData)
      ? getNeighboursAroundAnchor({ anchor, anchorData })
      : getNeighboursBeyondAnchor(anchor)),
  }
}
